// Surface-agnostic deeplink core shared by CustomerDeeplinks and GuestDeeplinks.
// Holds everything independent of the surface: web-URL building from an injected
// base, inbound URL resolution, and AASA/App-Links pattern export. The
// surface-specific native/app link (scheme + identifiers) is the one abstract seam.
/** A universal deeplink opens the app on every platform via a tapped https link. */
export function isUniversalCoverage(platforms) {
    return platforms.web && platforms.ios && platforms.android;
}
function matchPath(pattern, pathname) {
    // filter(Boolean) drops leading/trailing empty segments, so a trailing slash
    // (or a leading one) matches the same as its slash-free form.
    const patternParts = pattern.split('/').filter(Boolean);
    const pathParts = pathname.split('/').filter(Boolean);
    if (patternParts.length !== pathParts.length)
        return null;
    const params = {};
    for (let i = 0; i < patternParts.length; i++) {
        const part = patternParts[i];
        if (part.startsWith(':')) {
            params[part.slice(1)] = decodeURIComponent(pathParts[i]);
        }
        else if (part !== pathParts[i]) {
            return null;
        }
    }
    return params;
}
// `base` is only a parse origin for root-relative inbound paths; matching is on
// pathname alone, so the host is never compared.
function toUrl(input, base) {
    if (typeof input !== 'string')
        return input;
    try {
        return new URL(input);
    }
    catch {
        try {
            return new URL(input, base);
        }
        catch {
            return null;
        }
    }
}
/** Append `#fragment` to a link, or return it unchanged when there is none. */
export function appendFragment(link, fragment) {
    return fragment ? `${link}#${fragment}` : link;
}
/** AASA / Android App-Links glob for a path pattern (`:param` → `*`). */
export function toAasaGlob(pathPattern) {
    return pathPattern.replace(/:[^/]+/g, '*');
}
/** Reserved query param carrying a generation-time minted stable key, set by
 *  `buildUniversalLink` when a tracked handler has no declared one. Deliberately
 *  not a `meta.params` entry, so it defaults to both surfaces unscoped. */
export const MINTED_STABLE_KEY_PARAM = '_dlk';
/** Prepended to a minted key at resolve time (the raw value on the wire is
 *  unprefixed — the `_dlk` param name already marks it minted) so the resolved
 *  stable key distinguishes a minted (per-link-instance) key from a declared
 *  (per-entity) one, e.g. for warehouse dedup-tier reporting. */
export const MINTED_STABLE_KEY_PREFIX = 'dlk_';
// Earliest plausible mint time (2023-11) — lower bound for validating an
// inbound minted key's embedded timestamp.
const MINTED_STABLE_KEY_MIN_TS = 1_700_000_000_000;
const MINTED_STABLE_KEY_PATTERN = /^([0-9a-z]+)\.([0-9a-z]+)\.([0-9a-z]+)$/;
// FNV-1a (32-bit) — a standard non-cryptographic hash; the two constants are the
// published FNV offset-basis and prime. No shared secret (verification is
// source-readable by design), so this binds the minted key to surface + linkKey +
// timestamp and rejects garbage/replay, but not a determined forger. Residual
// abuse is handled downstream by excluding offending user agents.
const FNV_OFFSET_BASIS = 2166136261;
const FNV_PRIME = 16777619;
function hashToken(input) {
    let hash = FNV_OFFSET_BASIS;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, FNV_PRIME);
    }
    return (hash >>> 0).toString(36);
}
/**
 * Shared deeplink registry. A surface subclass fixes the param map, the handler
 * map, and its link options, and implements the one surface-specific seam
 * ({@link buildAppLink}). The package exports only the two concrete subclasses
 * (CustomerDeeplinks, GuestDeeplinks), so the hierarchy is effectively sealed.
 */
export class BaseDeeplinks {
    base;
    handlers;
    scheme;
    constructor(base, handlers, scheme) {
        this.base = base;
        this.handlers = handlers;
        this.scheme = scheme;
    }
    // Assemble a `<scheme>://<route>?<query>#<fragment>` link from a resolved
    // native route and its already surface-scoped query; null when no scheme is
    // set (ingest-only surfaces).
    buildSchemeLink(native, query, fragment) {
        if (!this.scheme)
            return null;
        const nativePath = native.startsWith('/') ? native.slice(1) : native;
        const schemeQuery = new URLSearchParams(query).toString();
        return appendFragment(`${this.scheme}://${nativePath}${schemeQuery ? `?${schemeQuery}` : ''}`, fragment);
    }
    // Location scoping defaults to true: only a handler that opts out
    // (`scopedToLocation: false`) has a native route that ignores the location.
    scopedToLocationFor(key) {
        const handlers = this.handlers;
        return handlers[key].scopedToLocation ?? true;
    }
    // Query params the handler pins to one surface; a name absent here applies to
    // both. Read from catalog meta.params, so an undeclared param is never
    // dropped — it simply applies to both surfaces, as query did before scoping.
    querySurfaces(key) {
        const handlers = this.handlers;
        const scoped = new Map();
        for (const param of handlers[key].meta?.params ?? []) {
            if (param.in === 'query' && param.surface) {
                scoped.set(param.name, param.surface);
            }
        }
        return scoped;
    }
    // The caller query entries that belong on `surface`: an entry applies unless
    // the handler scopes it to the other surface.
    scopedQuery(key, surface, options) {
        const surfaces = this.querySurfaces(key);
        const scoped = {};
        for (const [name, value] of Object.entries(options?.query ?? {})) {
            if ((surfaces.get(name) ?? surface) === surface)
                scoped[name] = value;
        }
        return scoped;
    }
    /**
     * Build both link forms for a destination.
     * @param key registered deeplink key
     * @param params typed params for that key
     * @param options query/fragment plus any surface-specific fields
     * @returns the web `url` and the native `appLink` (null when inapplicable)
     */
    buildUniversalLink(key, params, options) {
        // Widen to the canonical map so `[key]` correlates with K (an open generic
        // Handlers subtype does not narrow through index access).
        const handlers = this.handlers;
        const { path, query } = handlers[key].toWeb(params);
        const url = new URL(path, this.base);
        for (const [name, value] of Object.entries(query ?? {})) {
            url.searchParams.set(name, value);
        }
        // A universal, location-scoped destination carries the resolved location on
        // the web url too, so a tapped link (and resolveInbound) recovers the same
        // location the native app link embeds. Scoped to universal handlers because
        // only those are resolved inbound — partial/app-only links keep their
        // historical location-free web fallback.
        const meta = handlers[key].meta;
        const scopedToLocation = this.scopedToLocationFor(key);
        const isUniversal = meta ? isUniversalCoverage(meta.platforms) : false;
        if (scopedToLocation && isUniversal && options?.locationId) {
            url.searchParams.set('locationId', options.locationId);
        }
        // Handler params are authoritative; caller query only fills gaps, minus any
        // params the handler scopes to the app link (see scopedQuery).
        for (const [name, value] of Object.entries(this.scopedQuery(key, 'web', options))) {
            if (!url.searchParams.has(name))
                url.searchParams.set(name, value);
        }
        if (options?.fragment)
            url.hash = options.fragment;
        // Without this, a tracked handler with no declared key resolves inbound
        // with stableKey: null and the click is silently dropped (see
        // resolveTrackedClick).
        let appLinkOptions = options;
        if (meta?.tracked && !meta.trackingKey) {
            const minted = this.mintStableKey(String(key));
            url.searchParams.set(MINTED_STABLE_KEY_PARAM, minted);
            appLinkOptions = Object.assign({}, options, {
                query: { ...options?.query, [MINTED_STABLE_KEY_PARAM]: minted },
            });
        }
        return {
            url: url.toString(),
            appLink: this.buildAppLink(key, params, appLinkOptions),
        };
    }
    /**
     * Parse an inbound web URL (or bare `/brands/…` path) to a keyed deeplink.
     * @param input absolute URL, URL object, or root-relative path
     * @returns the resolved deeplink, or null if no handler matches
     */
    resolveInbound(input) {
        const url = toUrl(input, this.base);
        if (!url)
            return null;
        const fragment = url.hash ? url.hash.slice(1) : undefined;
        for (const handler of this.universalHandlers()) {
            if (!handler.resolve)
                continue;
            const pathParams = matchPath(handler.pathPattern, url.pathname);
            if (!pathParams)
                continue;
            const resolved = handler.resolve(pathParams, url.searchParams);
            if (!resolved)
                continue;
            const scopedToLocation = this.scopedToLocationFor(resolved.key);
            const locationId = scopedToLocation
                ? (url.searchParams.get('locationId') ?? undefined)
                : undefined;
            return {
                ...resolved,
                ...(locationId ? { locationId } : {}),
                ...(fragment ? { fragment } : {}),
            };
        }
        return null;
    }
    /**
     * Resolve an inbound URL to a keyed deeplink only when its handler opts into
     * click capture via `meta.tracked`, carrying the stable attribution key. The
     * gate for first-party click tracking: a non-null return is a tracked click.
     * Side effects stay in consumers — this is pure. Returns null for untracked or
     * unmatched links.
     * @param input absolute URL, URL object, or root-relative path
     */
    resolveTrackedClick(input) {
        const resolved = this.resolveInbound(input);
        if (!resolved)
            return null;
        const handlers = this.handlers;
        const meta = handlers[resolved.key].meta;
        if (!meta?.tracked)
            return null;
        return {
            ...resolved,
            stableKey: this.stableKeyFor(input, resolved, meta.trackingKey),
        };
    }
    // Query first (menu reorderId, inbox threadId), then resolved params. Used
    // only by stableKeyFor — a handler that declares a trackingKey is tracked
    // via that key alone and never mints or honors a minted _dlk.
    declaredStableKey(url, params, trackingKey) {
        if (!trackingKey)
            return null;
        const fromQuery = url?.searchParams.get(trackingKey);
        if (fromQuery)
            return fromQuery;
        return params[trackingKey] ?? null;
    }
    mintStableKey(linkKey) {
        const ts = Date.now().toString(36);
        const rand = (Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)).slice(0, 8);
        const sig = hashToken(`${ts}:${rand}:${this.surface}:${linkKey}`);
        return `${ts}.${rand}.${sig}`;
    }
    // True only for a value we minted for THIS surface + linkKey whose embedded
    // timestamp is in range. Not forgery-proof (no secret) but rejects garbage,
    // cross-link replay, and accidental/bot fabrication.
    isValidMintedKey(value, linkKey) {
        const match = MINTED_STABLE_KEY_PATTERN.exec(value);
        if (!match)
            return false;
        const [, ts, rand, sig] = match;
        const ms = Number.parseInt(ts, 36);
        if (ms < MINTED_STABLE_KEY_MIN_TS || ms > Date.now() + 86_400_000) {
            return false;
        }
        return sig === hashToken(`${ts}:${rand}:${this.surface}:${linkKey}`);
    }
    // Declared key wins; a handler declaring a trackingKey is tracked only via
    // that key, so an inbound _dlk on it is fabricated and never honored.
    // Otherwise fall back to the minted key, prefixed here to distinguish a
    // per-link-instance mint from a per-entity declared key downstream.
    stableKeyFor(input, resolved, trackingKey) {
        const url = toUrl(input, this.base);
        const declared = this.declaredStableKey(url, resolved.params, trackingKey);
        if (declared)
            return declared;
        if (trackingKey)
            return null;
        const raw = url?.searchParams.get(MINTED_STABLE_KEY_PARAM);
        if (!raw || !this.isValidMintedKey(raw, String(resolved.key)))
            return null;
        return `${MINTED_STABLE_KEY_PREFIX}${raw}`;
    }
    /**
     * AASA / Android App-Links globs for every universal deeplink (`:param` → `*`).
     * Non-universal (partial/web-only/app-only) catalog entries are excluded — they
     * must never be advertised for app capture.
     * @returns one glob pattern per registered universal path
     */
    registeredPathPatterns() {
        return this.universalHandlers().map((handler) => toAasaGlob(handler.pathPattern));
    }
    // Object.values erases the key types of a mapped handler map, so re-assert the
    // element type once here rather than at each call site.
    handlerList() {
        return Object.values(this.handlers);
    }
    // AASA registration and inbound resolution are universal-only; catalog-only
    // entries (partial/web-only/app-only) participate in neither.
    universalHandlers() {
        return this.handlerList().filter((handler) => handler.meta ? isUniversalCoverage(handler.meta.platforms) : false);
    }
}
//# sourceMappingURL=base.js.map